import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Input, List, Avatar, Space, message, Empty, Tag, Select } from 'antd';
import { SoundOutlined, SendOutlined, UserOutlined, AlertOutlined, NotificationOutlined, ToolOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';

import ListPageHeader from '@shared/ListPageHeader';
import operatorApiFetch from '@services/operator/apiService';

dayjs.extend(relativeTime);
dayjs.locale('zh-tw');

const { TextArea } = Input;

// 廣播類型配置
const BROADCAST_TYPE_CONFIG = {
  announcement: { color: 'blue', icon: NotificationOutlined },
  task: { color: 'orange', icon: ToolOutlined },
  urgent: { color: 'red', icon: AlertOutlined },
  task_completed: { color: 'green', icon: CheckCircleOutlined },
};

const WarehouseBroadcastsList = () => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [broadcastType, setBroadcastType] = useState('announcement');
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
  const [assigneeList, setAssigneeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // 載入廣播歷史
  const loadBroadcasts = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await operatorApiFetch('warehouse_broadcasts.json');
      const data = await response.json();
      if (data.success) {
        setBroadcasts(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load broadcasts:', error);
    } finally {
      setListLoading(false);
    }
  }, []);

  // 載入可指派對象
  const loadAssignees = useCallback(async () => {
    try {
      const response = await operatorApiFetch('warehouse_broadcasts/assignees.json');
      const data = await response.json();
      if (data.success) {
        setAssigneeList(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load assignees:', error);
    }
  }, []);

  useEffect(() => {
    loadBroadcasts();
    loadAssignees();
  }, [loadBroadcasts, loadAssignees]);

  // 發送廣播
  const handleSend = async () => {
    if (!content.trim()) {
      message.warning(t('operator.pages.warehouse_broadcasts.content_required'));
      return;
    }

    setLoading(true);
    try {
      const body = {
        content,
        broadcast_type: broadcastType,
      };

      // 任務類型時附帶指派對象
      if (broadcastType === 'task' && selectedAssigneeIds.length > 0) {
        body.assignee_names = assigneeList
          .filter(op => selectedAssigneeIds.includes(op.id))
          .map(op => op.name);
      }

      const response = await operatorApiFetch('warehouse_broadcasts.json', {
        method: 'POST',
        body,
      });
      const data = await response.json();

      if (data.success) {
        message.success(t('operator.pages.warehouse_broadcasts.send_success'));
        setContent('');
        setSelectedAssigneeIds([]);
        loadBroadcasts();
      } else {
        message.error(data.message || t('common.error.unknown'));
      }
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      message.error(t('common.error.unknown'));
    } finally {
      setLoading(false);
    }
  };

  const assigneeOptions = assigneeList.map(op => ({
    label: op.name,
    value: op.id,
  }));

  const broadcastTypeOptions = [
    { label: t('operator.pages.warehouse_broadcasts.type_announcement'), value: 'announcement' },
    { label: t('operator.pages.warehouse_broadcasts.type_task'), value: 'task' },
    { label: t('operator.pages.warehouse_broadcasts.type_urgent'), value: 'urgent' },
  ];

  const renderTypeTag = (type) => {
    const config = BROADCAST_TYPE_CONFIG[type] || BROADCAST_TYPE_CONFIG.announcement;
    const Icon = config.icon;
    return (
      <Tag color={config.color} icon={<Icon />}>
        {t(`operator.pages.warehouse_broadcasts.type_${type}`)}
      </Tag>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ListPageHeader items={['org_management', 'org_management.warehouse_broadcasts']} />
      {/* 發送廣播 */}
      <Card
        title={
          <Space>
            <SoundOutlined />
            {t('operator.pages.warehouse_broadcasts.send_title')}
          </Space>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{t('operator.pages.warehouse_broadcasts.type_label')}:</span>
            <Select
              value={broadcastType}
              onChange={(val) => {
                setBroadcastType(val);
                if (val !== 'task') setSelectedAssigneeIds([]);
              }}
              options={broadcastTypeOptions}
              style={{ width: 120 }}
            />
            {broadcastType === 'task' && (
              <>
                <span style={{ marginLeft: 8 }}>{t('operator.pages.warehouse_broadcasts.assignees')}:</span>
                <Select
                  mode="multiple"
                  value={selectedAssigneeIds}
                  onChange={setSelectedAssigneeIds}
                  options={assigneeOptions}
                  placeholder={t('operator.pages.warehouse_broadcasts.select_assignees')}
                  style={{ minWidth: 200 }}
                  allowClear
                  optionFilterProp="label"
                />
              </>
            )}
          </div>

          <TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('operator.pages.warehouse_broadcasts.content_placeholder')}
            rows={3}
            maxLength={500}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#999' }}>
              {content.length} / 500
            </span>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={loading}
              onClick={handleSend}
              disabled={!content.trim()}
            >
              {t('operator.pages.warehouse_broadcasts.send_button')}
            </Button>
          </div>
        </div>
      </Card>

      {/* 廣播歷史 */}
      <Card
        title={t('operator.pages.warehouse_broadcasts.history_title')}
        loading={listLoading}
      >
        {broadcasts.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={broadcasts}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={
                    <Space>
                      {renderTypeTag(item.broadcast_type)}
                      <span>{item.sender_name}</span>
                    </Space>
                  }
                  description={
                    <div>
                      {item.broadcast_type === 'task' && item.assignee_names?.length > 0 && (
                        <div style={{ marginBottom: 4, color: '#666' }}>
                          {t('operator.pages.warehouse_broadcasts.assignees')}: {item.assignee_names.join(', ')}
                        </div>
                      )}
                      <div style={{ marginBottom: 4 }}>{item.content}</div>
                      <div style={{ color: '#999', fontSize: 12 }}>
                        {dayjs(item.created_at).fromNow()}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty description={t('operator.pages.warehouse_broadcasts.no_history')} />
        )}
      </Card>
    </div>
  );
};

export default WarehouseBroadcastsList;
